/**
 * Client forensic telemetry (QPN-2111 / Wave 2a).
 * Public API: trackUiAction, trackRouteChange, trackClientError, initClientTelemetry.
 * Flush is internal only — do not export flush for app code.
 */

import axiosInstance from '../api/axiosInstance';
import { getJourneyId } from './journeyId';
import {
  TELEMETRY_ACTION_VALUES,
  TelemetryEventTypes,
} from '../enums/TelemetryActionsEnum';

const MAX_QUEUE = 50;
const FLUSH_SIZE = 20;
const FLUSH_INTERVAL_MS = 30000;
const MAX_PATH_LEN = 512;
const MAX_MESSAGE_LEN = 500;
const MAX_STACK_LEN = 2000;
const MAX_NAME_LEN = 64;
const CLIENT_ERROR_DEDUP_MS = 5000;
const CLIENT_ERROR_PER_MIN = 5;
const EXTENSION_URL_RE =
  /^(chrome|moz|safari|safari-web)-extension:\/\/|webkit-masked-url:/i;
const OPAQUE_SCRIPT_ERROR_RE = /^script error\.?$/i;

/** @type {object[]} */
let queue = [];
let flushTimer = null;
let initialized = false;
let lastRoutePath = null;
let unknownActionWarned = false;
let clientErrorCapWarned = false;
/** @type {Map<string, number>} */
const recentClientErrors = new Map();
/** @type {number[]} */
const clientErrorTimestamps = [];

function isTelemetryEnabled() {
  return process.env.REACT_APP_CLIENT_TELEMETRY !== 'false';
}

function truncate(value, max) {
  if (value == null) return undefined;
  const str = String(value);
  return str.length <= max ? str : str.slice(0, max);
}

function sanitizeMeta(meta) {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return undefined;
  // Keys are compared lowercased — entries must be lowercase
  const denied = new Set([
    'password',
    'token',
    'authorization',
    'cookie',
    'secret',
    'apikey',
    'api_key',
  ]);
  const out = {};
  for (const [key, value] of Object.entries(meta)) {
    if (denied.has(String(key).toLowerCase())) continue;
    if (value == null) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = typeof value === 'string' ? truncate(value, 256) : value;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function currentPath() {
  try {
    return truncate(
      `${window.location.pathname}${window.location.search || ''}`,
      MAX_PATH_LEN
    );
  } catch {
    return undefined;
  }
}

function getCookie(name) {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
  } catch {
    /* ignore */
  }
  return null;
}

function getCsrfToken() {
  return getCookie('XSRF-TOKEN');
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushInternal();
  }, FLUSH_INTERVAL_MS);
}

function enqueue(event) {
  if (!isTelemetryEnabled()) return;
  queue.push(event);
  if (queue.length > MAX_QUEUE) {
    queue.splice(0, queue.length - MAX_QUEUE);
  }
  if (queue.length >= FLUSH_SIZE) {
    void flushInternal();
  } else {
    scheduleFlush();
  }
}

async function flushInternal() {
  if (!isTelemetryEnabled()) {
    queue = [];
    return;
  }
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const batch = queue.splice(0, FLUSH_SIZE);
  let journeyId;
  try {
    journeyId = getJourneyId();
  } catch {
    queue = batch.concat(queue).slice(0, MAX_QUEUE);
    return;
  }

  const body = {
    journeyId,
    events: batch,
  };

  try {
    const res = await axiosInstance.post('/telemetry', body, {
      // Telemetry must not trigger session-expiry redirects / toasts
      validateStatus: (status) => status >= 200 && status < 500,
    });
    // Re-queue auth failures (e.g. still on login) and rate limits so batches are not lost.
    // Do not gate on XSRF-TOKEN — CSRF cookie middleware is often disabled locally.
    if (res?.status === 401 || res?.status === 403 || res?.status === 429) {
      queue = batch.concat(queue).slice(0, MAX_QUEUE);
      scheduleFlush();
    }
  } catch {
    // Fail soft — drop this batch (do not re-queue forever)
  }
}

/**
 * Best-effort unload flush with credentials + CSRF (keepalive fetch).
 * @returns {void}
 */
function flushOnHide() {
  if (!isTelemetryEnabled() || queue.length === 0) return;
  let journeyId;
  try {
    journeyId = getJourneyId();
  } catch {
    return;
  }
  const batch = queue.splice(0, FLUSH_SIZE);
  const payload = JSON.stringify({ journeyId, events: batch });
  const headers = {
    'Content-Type': 'application/json',
    'X-Journey-Id': journeyId,
  };
  const csrf = getCsrfToken();
  if (csrf) headers['X-CSRF-Token'] = csrf;

  const url = `${process.env.REACT_APP_API_URL}/telemetry`;
  try {
    if (typeof fetch === 'function') {
      void fetch(url, {
        method: 'POST',
        body: payload,
        headers,
        credentials: 'include',
        keepalive: true,
      });
      return;
    }
  } catch {
    /* fall through */
  }
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    }
  } catch {
    /* ignore */
  }
}

function looksFirstParty(filename, stack) {
  const hay = `${filename || ''}\n${stack || ''}`;
  if (!hay.trim()) return false;
  if (EXTENSION_URL_RE.test(hay)) return false;
  try {
    const origin = window.location.origin;
    if (origin && hay.includes(origin)) return true;
  } catch {
    /* ignore */
  }
  // CRA / webpack chunk paths
  if (/\/static\/js\//i.test(hay) || /frontend\/src\//i.test(hay)) return true;
  return false;
}

/**
 * @param {{ message?: string, filename?: string, stack?: string, source?: string }} input
 * @returns {boolean}
 */
export function shouldAcceptClientError(input = {}) {
  const message = String(input.message || '');
  const source = input.source || 'window';
  if (source === 'ErrorBoundary') return true;
  if (OPAQUE_SCRIPT_ERROR_RE.test(message.trim())) return false;
  if (EXTENSION_URL_RE.test(String(input.filename || ''))) return false;
  if (EXTENSION_URL_RE.test(String(input.stack || ''))) return false;
  if (/ResizeObserver loop/i.test(message)) return false;
  return looksFirstParty(input.filename, input.stack);
}

function withinClientErrorRateLimit() {
  const now = Date.now();
  while (clientErrorTimestamps.length && now - clientErrorTimestamps[0] > 60000) {
    clientErrorTimestamps.shift();
  }
  if (clientErrorTimestamps.length >= CLIENT_ERROR_PER_MIN) {
    if (!clientErrorCapWarned) {
      clientErrorCapWarned = true;
      // eslint-disable-next-line no-console
      console.warn('[telemetry] CLIENT_ERROR rate cap reached for this tab');
    }
    return false;
  }
  clientErrorTimestamps.push(now);
  return true;
}

function isDuplicateClientError(message, path) {
  const key = `${message}::${path || ''}`;
  const now = Date.now();
  const prev = recentClientErrors.get(key);
  if (prev != null && now - prev < CLIENT_ERROR_DEDUP_MS) return true;
  recentClientErrors.set(key, now);
  return false;
}

/**
 * @param {string} name - Must be a TelemetryActions value
 * @param {Record<string, unknown>} [meta]
 */
export function trackUiAction(name, meta) {
  if (!isTelemetryEnabled()) return;
  if (!TELEMETRY_ACTION_VALUES.includes(name)) {
    if (!unknownActionWarned) {
      unknownActionWarned = true;
      // eslint-disable-next-line no-console
      console.warn('[telemetry] Dropped unknown UI_ACTION name (use TelemetryActions constants)');
    }
    return;
  }
  enqueue({
    type: TelemetryEventTypes.UI_ACTION,
    clientTs: Date.now(),
    name: truncate(name, MAX_NAME_LEN),
    path: currentPath(),
    meta: sanitizeMeta(meta),
  });
}

/**
 * @param {string} path
 * @param {string} [fromPath]
 */
export function trackRouteChange(path, fromPath) {
  if (!isTelemetryEnabled()) return;
  const next = truncate(path, MAX_PATH_LEN);
  if (!next) return;
  if (next === lastRoutePath) return;
  const prev = lastRoutePath;
  lastRoutePath = next;
  enqueue({
    type: TelemetryEventTypes.ROUTE_CHANGE,
    clientTs: Date.now(),
    path: next,
    fromPath: fromPath != null ? truncate(fromPath, MAX_PATH_LEN) : prev || undefined,
  });
}

/**
 * @param {{ message?: string, filename?: string, stack?: string, source?: string }} errorInfo
 */
export function trackClientError(errorInfo = {}) {
  if (!isTelemetryEnabled()) return;
  if (!shouldAcceptClientError(errorInfo)) return;
  const message = truncate(errorInfo.message || 'Unknown error', MAX_MESSAGE_LEN);
  const path = currentPath();
  if (isDuplicateClientError(message, path)) return;
  if (!withinClientErrorRateLimit()) return;
  enqueue({
    type: TelemetryEventTypes.CLIENT_ERROR,
    clientTs: Date.now(),
    path,
    message,
    source: truncate(errorInfo.source || 'window', 64),
    stack: truncate(errorInfo.stack, MAX_STACK_LEN),
  });
}

function onVisibilityChange() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    flushOnHide();
  }
}

/**
 * Install route-independent listeners once (errors + lifecycle flush).
 */
export function initClientTelemetry() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  if (!isTelemetryEnabled()) return;

  window.addEventListener('error', (event) => {
    trackClientError({
      message: event.message,
      filename: event.filename,
      stack: event.error?.stack,
      source: 'window.onerror',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    trackClientError({
      message: reason?.message || String(reason || 'Unhandled rejection'),
      stack: reason?.stack,
      source: 'unhandledrejection',
    });
  });

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', flushOnHide);
}

/** @returns {void} */
export function __flushForTests() {
  return flushInternal();
}

/** @returns {void} */
export function __resetTelemetryForTests() {
  queue = [];
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  initialized = false;
  lastRoutePath = null;
  unknownActionWarned = false;
  clientErrorCapWarned = false;
  recentClientErrors.clear();
  clientErrorTimestamps.length = 0;
}
